import { SortTh } from '../../components/SortTh';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { formatCents, ORDER_STATUS_LABELS, ORDER_STATUSES, orderOverdue, type OrderStatus, type OrderSummary, type Paginated } from '@kontor/shared';
import { api, queryString } from '../../api';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { useSession } from '../../session';
import './orders.css';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const day = (d: string) => dateFormat.format(new Date(`${d}T12:00:00`));

export const orderTone = (s: OrderStatus) => `order-status order-status--${s}`;

/** Stand und Leistungsdatum passen nicht zusammen (zum Beispiel „Geplant“ mit einem Datum von letzter Woche), sonst null */
const overdueOf = (o: { status: OrderStatus; serviceStart: string | null; serviceEnd: string | null }) => orderOverdue(o);

/** Leistungszeitraum als kurzer Text, „–“ ohne Angabe */
export function serviceRange(start: string | null, end: string | null): string {
  if (!start && !end) return '–';
  if (start && end && start !== end) return `${day(start)} – ${day(end)}`;
  return day((start ?? end)!);
}

/** Liste der Aufträge mit Suche und Filter nach Stand (KON-131) */
export function OrdersPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const page = Number(params.get('page') ?? 1);
  const sort = params.get('sort') ?? '-createdAt';

  const list = useQuery({
    queryKey: ['orders', { q, status, page, sort }],
    queryFn: () => api<Paginated<OrderSummary>>('GET', `/orders${queryString({ q, status, page, sort, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const onSort = (next: string) => update({ sort: next === '-createdAt' ? '' : next });
  const fields: FilterField[] = [{ key: 'status', label: 'Stand', type: 'select', options: [{ value: '', label: 'Alle' }, ...ORDER_STATUSES.map((s) => ({ value: s, label: ORDER_STATUS_LABELS[s] }))] }];

  return (
    <>
      <div className="page-head">
        <h1>Aufträge</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} insgesamt` : ''}</span>
        {can('orders.write') && <Link className="btn btn--primary btn--sm page-head__action" to="/auftraege/neu">Neuer Auftrag</Link>}
      </div>
      <FilterBar
        search={{ value: q, placeholder: 'Nummer, Betreff, Kunde oder Bestellnummer', onSubmit: (v) => update({ q: v }) }}
        fields={fields}
        values={{ status }}
        onChange={(key, value) => update({ [key]: value })}
        onReset={() => setParams(new URLSearchParams())}
      />
      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <SortTh label="Auftrag" sortKey="number" sort={sort} onSort={onSort} />
              <SortTh label="Kunde" sortKey="contact" sort={sort} onSort={onSort} />
              <SortTh label="Stand" sortKey="status" sort={sort} onSort={onSort} />
              <SortTh label="Leistung" sortKey="service" sort={sort} onSort={onSort} first="desc" />
              <SortTh label="Brutto" sortKey="gross" sort={sort} onSort={onSort} first="desc" className="num" />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={5} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && (
              <tr><td colSpan={5} className="empty">{q || status ? 'Keine Aufträge zu dieser Suche.' : 'Noch keine Aufträge. Sie entstehen aus angenommenen Angeboten oder über „Neuer Auftrag“.'}</td></tr>
            )}
            {list.data?.data.map((o) => (
              <ExpandRow
                key={o.id}
                summary={
                  <Summary
                    title={<Link to={`/auftraege/${o.id}`} className="strong-link">{o.number}</Link>}
                    aside={<><span className={orderTone(o.status)}>{ORDER_STATUS_LABELS[o.status]}</span>{overdueOf(o) && <span className="order-overdue-badge">Überfällig</span>}</>}
                    line2={`${o.contactName} · ${o.subject}`}
                    line3={[serviceRange(o.serviceStart, o.serviceEnd), o.grossCents === null ? null : formatCents(o.grossCents)].filter((x) => x && x !== '–').join(' · ') || undefined}
                  />
                }
              >
                <td className="cell-desktop-title">
                  <Link to={`/auftraege/${o.id}`} className="strong-link">{o.number}</Link>
                  <div className="muted small">{o.subject}</div>
                </td>
                <td data-label="Kunde">{o.contactName}</td>
                <td data-label="Stand">
                  <span className={orderTone(o.status)}>{ORDER_STATUS_LABELS[o.status]}</span>
                  {overdueOf(o) && <span className="order-overdue-badge" title={overdueOf(o)!.text}>Überfällig</span>}
                </td>
                <td data-label="Leistung" className={overdueOf(o) ? 'order-overdue-date' : undefined}>
                  {serviceRange(o.serviceStart, o.serviceEnd)}
                  {overdueOf(o) && <div className="small order-overdue-hint">{overdueOf(o)!.short}</div>}
                </td>
                <td data-label="Brutto" className="num">{o.grossCents === null ? "–" : formatCents(o.grossCents)}</td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}
